import { useCallback, useEffect, useState } from "react";
import { Banner, LoadingLine, Spinner } from "../ui/common";
import { dynamo_configured, dynamo_scan_base_profiles } from "../lib/dynamo";
import { delete_session, forget_session } from "../lib/session_sync";

/**
 * Admin (user-management) page. Reached ONLY via a `#<password>_admin` URL hash (see
 * `is_admin_url`), so it is already behind the startup password gate — by the time this renders the
 * secrets are loaded and DynamoDB is configured. It scans EVERY person in the cloud table (not the
 * origin-gated `list_cloud_people`, since an admin must see everyone) and lets them be deleted.
 *
 * Deletion is permanent: it removes the person's base/run/times items from DynamoDB and forgets
 * them from this browser's local registry.
 */
export function AdminPage({ onExit }: { onExit: () => void }) {
  const [people, setPeople] = useState<Array<{ slug: string; first_name: string }> | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoadError(null);
    if (!dynamo_configured()) {
      setLoadError("DynamoDB is not configured — cloud storage is unavailable this session.");
      setPeople([]);
      return;
    }
    try {
      const list = await dynamo_scan_base_profiles();
      list.sort((a, b) => a.first_name.localeCompare(b.first_name));
      setPeople(list);
    } catch (e: any) {
      setLoadError(e?.message ? String(e.message) : String(e));
      setPeople([]);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function remove(slug: string, first_name: string) {
    if (!confirm(`Permanently delete "${first_name}" (${slug}) from the cloud? This cannot be undone.`)) {
      return;
    }
    setActionError(null);
    setDeleting(slug);
    try {
      await delete_session(slug);
      forget_session(slug);
      setPeople((cur) => (cur ?? []).filter((p) => p.slug !== slug));
    } catch (e: any) {
      setActionError(e?.message ? String(e.message) : String(e));
    } finally {
      setDeleting(null);
    }
  }

  return (
    <main className="app-main">
      <header className="lagna-header">
        <div className="lagna-brand">
          <div className="logo-mark" aria-hidden>
            <div className="ring" />
            <div className="star" />
          </div>
          <div>
            <div className="eyebrow">Birth-Time Rectification · Admin</div>
            <div className="display-title">User management</div>
          </div>
        </div>
        <div className="toolbar-actions">
          <button className="mono" onClick={onExit}>
            ← Back to app
          </button>
        </div>
      </header>

      <div className="card" style={{ maxWidth: 640, margin: "4vh auto 0", padding: 24 }}>
        <h2 style={{ marginTop: 0, fontSize: 18 }}>Saved people</h2>
        <p style={{ color: "var(--ink2)", fontSize: 13, lineHeight: 1.5 }}>
          Everyone stored in the cloud table. Deleting a person permanently removes their base, run
          and times records from DynamoDB — this cannot be undone.
        </p>

        {loadError && <Banner kind="error">{loadError}</Banner>}
        {actionError && <Banner kind="error">{actionError}</Banner>}

        {people === null ? (
          <LoadingLine message="Loading people…" />
        ) : people.length === 0 ? (
          <Banner kind="info">No saved people found.</Banner>
        ) : (
          <ul style={{ listStyle: "none", padding: 0, margin: "12px 0 0", display: "flex", flexDirection: "column", gap: 8 }}>
            {people.map((p) => (
              <li
                key={p.slug}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 12,
                  padding: "10px 12px",
                  border: "1px solid var(--line)",
                  borderRadius: 8,
                }}
              >
                <div>
                  <div style={{ fontSize: 14, fontWeight: 500 }}>{p.first_name}</div>
                  <div style={{ fontFamily: "var(--mono)", fontSize: 11, color: "var(--ink3)" }}>{p.slug}</div>
                </div>
                <button
                  className="mono"
                  disabled={deleting === p.slug}
                  onClick={() => void remove(p.slug, p.first_name)}
                  style={{ color: "var(--danger-ink)" }}
                >
                  {deleting === p.slug ? <Spinner /> : "🗑 Delete"}
                </button>
              </li>
            ))}
          </ul>
        )}

        <div style={{ marginTop: 16 }}>
          <button className="mono" onClick={() => void load()} disabled={people === null}>
            ↻ Refresh
          </button>
        </div>
      </div>
    </main>
  );
}
